import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { adminUser } from '../data/mockData'
import { adminAccounts } from '../data/adminExtendedData'
import { canPermission, normalizePermissions } from '../data/adminPermissions'
import { downloadXlsx, tableToRows } from '../utils/exportExcel'

const ADMIN_KEY = 'difan-admin-accounts-v1'
const AUTO_HOST_ATTR = 'data-admin-export-host'

function currentPermissions() {
  try {
    const stored = JSON.parse(localStorage.getItem(ADMIN_KEY) || 'null')
    const accounts = Array.isArray(stored) ? stored : adminAccounts
    const current = accounts.find((item) => item.id === adminUser.id) || { ...adminUser }
    return normalizePermissions(current.role || adminUser.role, current.permissions)
  } catch {
    return normalizePermissions(adminUser.role)
  }
}

function pageTitle() {
  return document.querySelector('.admin-content h1')?.textContent?.trim() || 'DIFAN 관리자 내보내기'
}
function targetTable() {
  const tables = Array.from(document.querySelectorAll('.admin-content table.data-table'))
  return tables.find((table) => table.offsetParent !== null) || tables[0] || null
}
function actionHost() {
  const content = document.querySelector('.admin-content')
  if (!content) return null
  const existing = content.querySelector('.page-title-actions, .admin-page-title__actions, .page-title > div:last-child, .table-toolbar')
  if (existing) return existing
  const titleRow = content.querySelector('.page-title-row')
  if (!titleRow) return null
  let host = titleRow.querySelector(`[${AUTO_HOST_ATTR}]`)
  if (!host) {
    host = document.createElement('div')
    host.className = 'page-title-actions'
    host.setAttribute(AUTO_HOST_ATTR, 'true')
    titleRow.appendChild(host)
  }
  return host
}

export default function AdminExcelExportEnhancer({ route }) {
  const [host, setHost] = useState(null)
  const [table, setTable] = useState(null)
  const [notice, setNotice] = useState('')
  const permissions = useMemo(() => currentPermissions(), [route])
  const dedicatedSensitiveSettlementExport = /^\/settlements\/[^/]+$/.test(String(route || '').split('?')[0])
  const allowed = !dedicatedSensitiveSettlementExport && canPermission(permissions, route, 'export')

  useEffect(() => {
    let frame = 0
    const refresh = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        setTable(targetTable())
        setHost(actionHost())
      })
    }
    refresh()
    const observer = new MutationObserver(refresh)
    const root = document.querySelector('.admin-content') || document.body
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      document.querySelectorAll(`[${AUTO_HOST_ATTR}]`).forEach((node) => node.remove())
    }
  }, [route])

  if (!allowed || !table || !host) return notice ? <div className="admin-global-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice('')}>×</button></div> : null

  const exportTable = () => {
    const headerCells = Array.from(table.querySelectorAll('thead th'))
    const excludeLastColumn = headerCells.at(-1)?.textContent?.trim() === '관리'
    const rows = tableToRows(table, { excludeLastColumn })
    if (!rows.length) return setNotice('내보낼 목록 데이터가 없습니다.')
    const title = pageTitle()
    downloadXlsx({
      filename: `difan_${title}_${new Date().toISOString().slice(0, 10)}`,
      sheetName: title,
      title,
      meta: [['화면', route], ['내보낸 시각', new Date().toLocaleString('ko-KR')]],
      rows,
    })
    setNotice(`${title} 목록을 Excel(.xlsx)로 내려받았습니다.`)
  }

  return <>
    {createPortal(<button type="button" className="button soft" onClick={exportTable}>엑셀 다운로드</button>, host)}
    {notice && <div className="admin-global-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice('')}>×</button></div>}
  </>
}
